import { useEffect, useState } from 'react'
import { Kbd } from './glyphs'
import { createTheme, deleteTheme, exportTheme, importTheme, themeById, TOKEN_GROUPS, updateTheme, useThemes, type Theme, type Tokens } from './themes'
import type { Suggestion } from './api'
import { addRule, auth, failureText, inbox, removeRule, type Policy, type Rule, type User } from './api'
import { Connect } from './connect'
import { applyTheme, setPrefs, store, useMail, usePrefs } from './store'

function Segmented<T extends string>({ value, options, onChange }: { value: T; options: Array<[T, string]>; onChange: (value: T) => void }) {
  return (
    <div className="segmented">
      {options.map(([key, text]) => <button key={key} className={key === value ? 'on' : ''} onClick={() => onChange(key)} type="button">{text}</button>)}
    </div>
  )
}

function SettingRow({ title, note, children }: { title: string; note?: React.ReactNode; children: React.ReactNode }) {
  return <div className="setting"><div className="setting-text"><span>{title}</span>{note && <span className="faint small">{note}</span>}</div>{children}</div>
}

export function Settings({ user, onTune }: { user: User; onTune?: () => void }) {
  const prefs = usePrefs()
  const [policy, setPolicy] = useState<Policy | null>(null)
  useEffect(() => { void inbox.policy().then(setPolicy).catch(() => {}) }, [])
  const updatePolicy = async (next: Partial<Policy>) => { setPolicy(previous => previous && { ...previous, ...next }); setPolicy(await inbox.setPolicy(next)) }

  return (
    <div className="settings">
      <div className="settings-col view-in">
        <section>
          <h2><span className="faint">1</span> Accounts</h2>
          <Connect firstRun={false} />
        </section>
        <Sorting onTune={onTune} />
        <section>
          <h2><span className="faint">3</span> Appearance</h2>
          <ThemeSettings current={prefs.theme} />
          <SettingRow title="Density" note={<>Switch any time with <Kbd k="⇧D" /></>}><Segmented value={prefs.density} options={[['compact', 'Compact'], ['comfortable', 'Comfortable']]} onChange={density => setPrefs({ density })} /></SettingRow>
          <SettingRow title="Right sidebar" note={<>Sender, history and recently opened. Toggle with <Kbd k={'\\'} /></>}><Segmented value={prefs.sidebar ? 'show' : 'hide'} options={[['show', 'Show'], ['hide', 'Hide']]} onChange={value => setPrefs({ sidebar: value === 'show' })} /></SettingRow>
        </section>
        <section>
          <h2><span className="faint">4</span> Reading and sending</h2>
          {policy && <>
            <SettingRow title="Load remote images" note="Trackers are always removed."><Segmented value={policy.remoteImages ? 'on' : 'off'} options={[['on', 'Always'], ['off', 'Never']]} onChange={value => void updatePolicy({ remoteImages: value === 'on' })} /></SettingRow>
            <SenderName />
            <SettingRow title="Undo send"><Segmented value={String(policy.undoSendSeconds)} options={[['0', 'Off'], ['5', '5 s'], ['10', '10 s'], ['30', '30 s']]} onChange={value => void updatePolicy({ undoSendSeconds: Number(value) })} /></SettingRow>
          </>}
        </section>
        <AgentAccess />
        <section>
          <h2><span className="faint">6</span> You</h2>
          <SettingRow title={user.name || user.email} note={user.email}><button className="link" onClick={() => { for (const key of Object.keys(localStorage)) if (key.startsWith('mm.snapshot')) localStorage.removeItem(key); void auth.signOut().then(() => location.assign('/')) }}>Sign out</button></SettingRow>
        </section>
      </div>
    </div>
  )
}

/** "Ryan Vogel <ryan@…>": the name recipients see. Saved when you leave the field or press Enter. */
function SenderName() {
  const [name, setName] = useState<string | null>(null)
  const [saved, setSaved] = useState('')
  useEffect(() => { void fetch('/api/sender-name').then(r => r.json()).then((d: { name: string }) => { setName(d.name); setSaved(d.name) }).catch(() => {}) }, [])
  const save = async () => {
    if (name === null || name.trim() === saved) return
    const response = await fetch('/api/sender-name', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name }) })
    if (response.ok) { const d = await response.json() as { name: string }; setName(d.name); setSaved(d.name) }
  }
  return (
    <SettingRow title="Your name" note={saved ? `Mail you send shows “${saved}” next to your address.` : 'Mail you send shows only your address.'}>
      <input className="agent-name your-name-input" value={name ?? ''} placeholder="e.g. Ryan Vogel" maxLength={100} onChange={event => setName(event.target.value)}
        onBlur={() => void save()} onKeyDown={event => { event.stopPropagation(); if (event.key === 'Enter') (event.target as HTMLInputElement).blur() }} />
    </SettingRow>
  )
}

/** Pick a theme, or copy one and edit every colour live. ⇧T cycles themes anywhere. */
function ThemeSettings({ current }: { current: string }) {
  const themes = useThemes()
  const [importing, setImporting] = useState(false)
  const [pasted, setPasted] = useState('')
  const [note, setNote] = useState('')
  const active = current === 'system' ? null : themeById(current) ?? null
  const flashNote = (text: string) => { setNote(text); setTimeout(() => setNote(''), 1800) }
  const pick = (id: string) => setPrefs({ theme: id })
  const duplicate = (from: Theme) => { const theme = createTheme(from); pick(theme.id) }
  const edit = (change: Parameters<typeof updateTheme>[1]) => { if (!active?.custom) return; updateTheme(active.id, change); applyTheme() }
  return (
    <div className="themes">
      <div className="setting-text"><span>Theme</span><span className="faint small">Cycle with <Kbd k="⇧T" />. Copy any theme to change its colours.</span></div>
      <div className="theme-grid">
        <ThemeCard name="System" selected={current === 'system'} onPick={() => pick('system')} tokens={null} />
        {themes.map(theme => <ThemeCard key={theme.id} name={theme.name} custom={theme.custom} selected={current === theme.id} onPick={() => pick(theme.id)} tokens={theme.tokens} />)}
      </div>
      <div className="theme-actions">
        {active && <button className="secondary" onClick={() => duplicate(active)}>{active.custom ? 'Duplicate' : `Customize ${active.name}`}</button>}
        {active && <button className="link small" onClick={() => void navigator.clipboard.writeText(exportTheme(active)).then(() => flashNote('Theme JSON copied'))}>Copy JSON</button>}
        <button className="link small" onClick={() => setImporting(!importing)}>{importing ? 'Cancel import' : 'Import JSON'}</button>
        {active?.custom && <button className="link small warn" onClick={() => { deleteTheme(active.id); pick('dark') }}>Delete</button>}
        {note && <span className="faint small">{note}</span>}
      </div>
      {importing && (
        <form className="rule-form" onSubmit={event => { event.preventDefault(); try { const theme = importTheme(pasted); pick(theme.id); setImporting(false); setPasted('') } catch { flashNote('That isn’t theme JSON.') } }}>
          <textarea rows={4} value={pasted} onChange={event => setPasted(event.target.value)} placeholder='{ "name": "My theme", "mode": "dark", "tokens": { "bg": "#1c1c1e", … } }' onKeyDown={event => event.stopPropagation()} />
          <div className="rule-actions"><span /><button className="primary" disabled={!pasted.trim()}>Import</button></div>
        </form>
      )}
      {active?.custom && (
        <div className="theme-editor">
          <div className="theme-editor-head">
            <input className="agent-name" value={active.name} maxLength={40} onChange={event => edit({ name: event.target.value })} onKeyDown={event => event.stopPropagation()} aria-label="Theme name" />
            <Segmented value={active.mode} options={[['dark', 'Dark'], ['light', 'Light']]} onChange={mode => edit({ mode })} />
          </div>
          {TOKEN_GROUPS.map(group => (
            <div key={group.title} className="token-group">
              <div className="token-group-title">{group.title}</div>
              <div className="token-grid">
                {group.keys.map(([key, label]) => <TokenInput key={key} label={label} value={active.tokens[key]} tag={group.title === 'Reason tags'} onChange={value => edit({ tokens: { [key]: value } })} />)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function ThemeCard({ name, tokens, selected, custom, onPick }: { name: string; tokens: Tokens | null; selected: boolean; custom?: boolean; onPick: () => void }) {
  const t = tokens
  return (
    <button className={`theme-card${selected ? ' on' : ''}`} onClick={onPick} type="button" aria-pressed={selected}>
      <span className="theme-preview" style={t ? { background: t.bg, borderColor: t.line } : { background: 'linear-gradient(135deg, #0b0b0b 50%, #ffffff 50%)' }}>
        {t && <>
          <span style={{ background: t.t1, width: '58%' }} />
          <span style={{ background: t.t3, width: '78%' }} />
          <span style={{ background: t.t5, width: '44%' }} />
          <i style={{ background: t.acc }} />
          {t.tagUrgent && <i style={{ background: t.tagUrgent, right: 22 }} />}
        </>}
      </span>
      <span className="theme-name">{name}{custom && <span className="faint"> · yours</span>}</span>
    </button>
  )
}

const HEX = /^#[0-9a-f]{6}$/i
/** A colour token: swatch picker for hex values, free text for anything CSS accepts (rgba, empty = automatic for tags). */
function TokenInput({ label, value, tag, onChange }: { label: string; value: string; tag: boolean; onChange: (value: string) => void }) {
  const [text, setText] = useState(value)
  useEffect(() => setText(value), [value])
  const commit = (next: string) => { const v = next.trim(); if (v === '' ? tag : CSS.supports('color', v)) onChange(v); else setText(value) }
  return (
    <label className="token">
      <span className="token-swatch" style={{ background: value || 'transparent' }}>
        {HEX.test(value) || !value ? <input type="color" value={HEX.test(value) ? value : '#888888'} onChange={event => { setText(event.target.value); onChange(event.target.value) }} /> : null}
        {!value && <span className="token-auto">A</span>}
      </span>
      <span className="token-label">{label}</span>
      <input className="token-text" value={text} placeholder={tag ? 'auto' : ''} spellCheck={false}
        onChange={event => setText(event.target.value)} onBlur={event => commit(event.target.value)}
        onKeyDown={event => { event.stopPropagation(); if (event.key === 'Enter') commit((event.target as HTMLInputElement).value) }} />
    </label>
  )
}

/** Rules Jev and Luna suggest from what you open, reply to and ignore. Nothing changes until you accept. */
function Suggestions() {
  const mail = useMail()
  const [busy, setBusy] = useState('')
  const [note, setNote] = useState('')
  const act = async (id: string, accept: boolean) => {
    setBusy(id)
    try {
      await fetch(`/api/learn/${encodeURIComponent(id)}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ accept }) })
      store.setSuggestions(mail.suggestions.filter(s => s.id !== id)); store.soon(50)
    } finally { setBusy('') }
  }
  const look = async () => {
    setBusy('run'); setNote('')
    try {
      const response = await fetch('/api/learn/run', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' })
      const result = await response.json() as { added?: number; suggestions?: Suggestion[]; error?: { message: string } }
      if (result.suggestions) store.setSuggestions(result.suggestions)
      store.soon(50)
      setNote(result.error ? result.error.message : result.added ? `Learned ${result.added} new rule${result.added === 1 ? '' : 's'}. They're applied below.` : 'Nothing clear-cut yet. Keep using the inbox and check back.')
    } finally { setBusy('') }
  }
  return (
    <div className="suggestions-block">
      <div className="suggestions-head">
        <span className="suggestions-title">✦ Jev learns</span>
        <span className="faint small">From what you open, reply to and clear, Jev adds rules on its own. They're marked Learned below; remove any you don't want.</span>
        <button className="link small" onClick={() => void look()} disabled={busy === 'run'}>{busy === 'run' ? 'Looking…' : 'Look for patterns now'}</button>
      </div>
      {mail.suggestions.map(s => (
        <div key={s.id} className="setting suggestion-row">
          <div className="setting-text">
            <span>{s.kind === 'text' ? s.pattern : s.kind === 'sender' ? `From ${s.pattern}` : `Anything from ${s.pattern}`}</span>
            <span className="faint small">{s.action === 'important' ? 'To Important' : `To Other · ${s.label}`} · {s.evidence}</span>
          </div>
          <div className="suggestion-actions">
            <button className="link small" disabled={!!busy} onClick={() => void act(s.id, false)}>Dismiss</button>
            <button className="secondary" disabled={!!busy} onClick={() => void act(s.id, true)}>Accept</button>
          </div>
        </div>
      ))}
      {!mail.suggestions.length && !note && <div className="faint small">{mail.learned.length ? `Learned ${mail.learned.length} rule${mail.learned.length === 1 ? '' : 's'} in the last two weeks.` : 'Nothing learned yet. It needs a few days of normal use.'}</div>}
      {note && <div className="faint small">{note}</div>}
    </div>
  )
}

interface AgentToken { id: string; name: string; scope: 'read' | 'full'; createdAt: string; lastUsedAt: string | null }

/** Agents connect by signing in (OAuth); personal tokens remain for scripts. */
function AgentAccess() {
  const [state, setState] = useState<{ url: string; apps: AgentToken[]; tokens: AgentToken[] } | null>(null)
  const [showTokens, setShowTokens] = useState(false)
  const [name, setName] = useState('')
  const [scope, setScope] = useState<'full' | 'read'>('full')
  const [fresh, setFresh] = useState<{ id: string; token: string } | null>(null)
  const [copied, setCopied] = useState('')
  const [error, setError] = useState('')
  const load = () => fetch('/api/agent-tokens').then(r => r.json()).then(setState).catch(() => setError('Couldn’t load agent access.'))
  useEffect(() => { void load() }, [])
  const copy = (text: string, what: string) => { void navigator.clipboard.writeText(text).then(() => { setCopied(what); setTimeout(() => setCopied(''), 1500) }) }
  async function create() {
    setError('')
    const response = await fetch('/api/agent-tokens', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: name.trim() || 'Script', scope }) })
    if (!response.ok) { setError('Couldn’t create a token.'); return }
    setFresh(await response.json()); setName(''); void load()
  }
  async function revoke(id: string) {
    await fetch(`/api/agent-tokens/${encodeURIComponent(id)}`, { method: 'DELETE' })
    if (fresh?.id === id) setFresh(null)
    void load()
  }
  const when = (iso: string | null) => iso ? new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'never'
  const config = state && fresh ? JSON.stringify({ mcpServers: { 'minimal-mail': { type: 'http', url: state.url, headers: { Authorization: `Bearer ${fresh.token}` } } } }, null, 2) : ''
  const row = (item: AgentToken) => (
    <SettingRow key={item.id} title={item.name} note={`${item.scope === 'full' ? 'Full access' : 'Read only'} · since ${when(item.createdAt)} · last used ${when(item.lastUsedAt)}`}>
      <button className="link small" onClick={() => void revoke(item.id)}>Revoke</button>
    </SettingRow>
  )
  return (
    <section>
      <h2><span className="faint">5</span> Agent access</h2>
      <p className="faint small">Add this URL as an MCP server or custom connector in Claude, ChatGPT, Cursor or any MCP client. It will ask you to sign in and choose Full access or Read only.</p>
      {state && <SettingRow title="MCP server" note="OAuth sign-in, Streamable HTTP"><button className="link small mono" onClick={() => copy(state.url, 'url')}>{copied === 'url' ? 'Copied' : state.url}</button></SettingRow>}
      {state?.apps.length ? <h3 className="agent-sub">Connected apps</h3> : null}
      {state?.apps.map(row)}
      <button className="link small agent-toggle" onClick={() => setShowTokens(!showTokens)}>{showTokens ? 'Hide personal tokens' : `Personal tokens for scripts${state?.tokens.length ? ` (${state.tokens.length})` : ''}`}</button>
      {showTokens && <>
        {fresh && (
          <div className="agent-fresh">
            <div className="small">Copy this token now. It won’t be shown again.</div>
            <code className="agent-token">{fresh.token}</code>
            <div className="agent-fresh-actions">
              <button className="secondary" onClick={() => copy(fresh.token, 'token')}>{copied === 'token' ? 'Copied' : 'Copy token'}</button>
              <button className="secondary" onClick={() => copy(config, 'config')}>{copied === 'config' ? 'Copied' : 'Copy MCP config'}</button>
              <button className="link small" onClick={() => setFresh(null)}>Done</button>
            </div>
          </div>
        )}
        {state?.tokens.map(row)}
        <form className="rule-form" onSubmit={event => { event.preventDefault(); void create() }}>
          <input className="agent-name" value={name} onChange={event => setName(event.target.value)} placeholder="Token name, e.g. Nightly digest script" maxLength={60} />
          <div className="rule-actions">
            <Segmented value={scope} options={[['full', 'Full access'], ['read', 'Read only']]} onChange={value => setScope(value as 'full' | 'read')} />
            <button className="primary">Create token</button>
          </div>
        </form>
      </>}
      {error && <p className="warn small">{error}</p>}
    </section>
  )
}

/** Where to go after signing in: only our own OAuth consent page, so ?next can't send you elsewhere. */
export const afterSignIn = () => {
  const next = new URLSearchParams(location.search).get('next') ?? ''
  return /^\/oauth\/authorize(\?|$)/.test(next) ? next : '/'
}

export function SignIn({ googleSignIn, signups }: { googleSignIn: boolean; signups: boolean }) {
  const [mode, setMode] = useState<'in' | 'up'>('in')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState(new URLSearchParams(location.search).get('auth') === 'error' ? 'Sign-in didn’t work. Try again.' : '')
  const [busy, setBusy] = useState(false)

  async function submit() {
    setBusy(true); setError('')
    const result = mode === 'in'
      ? await auth.signIn.email({ email, password })
      : await auth.signUp.email({ name: name || email.split('@')[0]!, email, password })
    if (result.error) { setError(result.error.message || 'That didn’t work.'); setBusy(false); return }
    location.assign(afterSignIn())
  }

  return (
    <div className="signin">
      <div className="signin-col">
        <h1>Minimal Mail</h1>
        <p className="muted">{afterSignIn() !== '/' ? 'Sign in to let an app use your mail. You’ll choose what it can do next.' : 'Every inbox in one place, with what needs you first.'}</p>
        {googleSignIn && <button className="primary wide google" onClick={() => void auth.signIn.social({ provider: 'google', callbackURL: afterSignIn() })}>Continue with Google</button>}
        {googleSignIn && <div className="divider"><span>or</span></div>}
        <form onSubmit={event => { event.preventDefault(); void submit() }}>
          {mode === 'up' && <label className="input-label"><span>Name</span><input value={name} onChange={event => setName(event.target.value)} autoComplete="name" /></label>}
          <label className="input-label"><span>Email</span><input type="email" value={email} onChange={event => setEmail(event.target.value)} autoComplete="username" required /></label>
          <label className="input-label"><span>Password</span><input type="password" value={password} onChange={event => setPassword(event.target.value)} autoComplete={mode === 'in' ? 'current-password' : 'new-password'} minLength={10} required /></label>
          <button className="secondary wide" disabled={busy}>{busy ? '…' : mode === 'in' ? 'Sign in' : 'Create account'}</button>
        </form>
        {signups && <button className="link small" onClick={() => setMode(mode === 'in' ? 'up' : 'in')}>{mode === 'in' ? 'New here? Create an account' : 'Have an account? Sign in'}</button>}
        {error && <p className="warn">{error}</p>}
      </div>
    </div>
  )
}

/** Taught rules: what Jev checks every email against, and exact senders. */
function Sorting({ onTune }: { onTune?: () => void }) {
  const mail = useMail()
  const [text, setText] = useState('')
  const [action, setAction] = useState<Rule['action']>('other')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const rules = [...mail.rules].reverse()
  async function add() {
    setBusy(true); setError('')
    try { await addRule({ kind: 'text', pattern: text, action }); setText(''); store.soon(50) } catch (failure) { setError(failureText(failure)) } finally { setBusy(false) }
  }
  async function remove(rule: Rule) {
    try { await removeRule(rule.id); store.soon(50) } catch (failure) { setError(failureText(failure)) }
  }
  return (
    <section>
      <h2><span className="faint">2</span> Sorting</h2>
      <p className="faint small">Describe mail in your own words and Jev re-sorts everything, new and old. Use Teach on any email to correct it.</p>
      {onTune && <button className="secondary tune-start" onClick={onTune}>Tune with 10 emails <Kbd k="g t" /></button>}
      {mail.triage.pending > 0 && <p className="muted small">Re-sorting {mail.triage.pending} email{mail.triage.pending === 1 ? '' : 's'}…</p>}
      <Suggestions />
      {rules.map(rule => (
        <div key={rule.id} className="setting rule">
          <div className="setting-text">
            <span>{rule.kind === 'age' ? `Mail older than ${rule.pattern} days` : rule.kind === 'text' ? rule.pattern : rule.kind === 'sender' ? `From ${rule.pattern}` : `Anything from ${rule.pattern}`}</span>
            <span className="faint small">{rule.learned && <span className="learned-badge">Learned</span>}{rule.action === 'other' ? `To Other · ${rule.label}` : 'To Important'}{rule.kind === 'text' ? ' · checked by Jev' : rule.kind === 'age' ? '' : ' · exact match'}{rule.evidence ? ` · ${rule.evidence}` : ''}</span>
          </div>
          <button className="link small" onClick={() => void remove(rule)}>Remove</button>
        </div>
      ))}
      <form className="rule-form" onSubmit={event => { event.preventDefault(); void add() }}>
        <textarea rows={2} value={text} onChange={event => setText(event.target.value)} placeholder="e.g. Newsletters about crypto, or automated outreach sent to my support addresses" />
        <div className="rule-actions">
          <div className="segmented">
            <button type="button" className={action === 'other' ? 'on' : ''} onClick={() => setAction('other')}>To Other</button>
            <button type="button" className={action === 'important' ? 'on' : ''} onClick={() => setAction('important')}>To Important</button>
          </div>
          <button className="primary" disabled={busy || text.trim().length < 8}>{busy ? 'Adding…' : 'Add rule'}</button>
        </div>
        {error && <p className="warn small">{error}</p>}
      </form>
    </section>
  )
}
